import React, { useCallback, useMemo } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import { MultiLevelSelect } from 'modules/menu/components/MultiLevelSelect';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { BaseField } from './types';
import { FormRowRenderer } from '../FormRowRenderer';
import { DisplayText, StyledInputContainer } from '../OfferDetailsFormFields.styles';
import { BasicDetails, FormValues } from '../types';
import { ProductHierarchyOption } from '../utils';

type ProductHierarchyProps = BaseField & {
  productHierarchyOptions: ProductHierarchyOption[];
  handleRestrictedPropertyChange: ({
    productHierarchyLevelId,
    alcoholPercentage,
  }: {
    productHierarchyLevelId?: string | null | undefined;
    alcoholPercentage?: string | number | undefined;
  }) => void;
};

const getHierarchyLabelPath = (
  hierarchyLevels: ProductHierarchyOption[],
  id: string = '',
  path: string[] = [],
): string[] => {
  const currentLevel = hierarchyLevels.find(level => level.value === id);
  if (!currentLevel) {
    return path;
  }
  const currentPath = [currentLevel.label, ...path];
  return !currentLevel.parentId
    ? currentPath
    : getHierarchyLabelPath(hierarchyLevels, currentLevel.parentId, currentPath);
};

const truncateStringFromStart = (str: string) => {
  const maxLength = 35;
  if (str.length <= maxLength) {
    return str;
  }
  return '...' + str.slice(-maxLength);
};

export const ProductHierarchy = ({
  sidePanelMode,
  productHierarchyOptions,
  handleRestrictedPropertyChange,
  offer,
}: ProductHierarchyProps) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { values } = useFormState<FormValues>();
  const form = useForm<FormValues>();
  const intl = useIntl();

  const isOverTheCounterProductHierarchyLevel = useCallback(
    (value: string): boolean =>
      !!productHierarchyOptions.find(option => option.value === value)?.tags.includes('over_the_counter'),
    [productHierarchyOptions],
  );

  const productHierarchyDisplayName = useMemo(
    () => getHierarchyLabelPath(productHierarchyOptions, values.productHierarchyLevelId).join(' / '),
    [productHierarchyOptions, values.productHierarchyLevelId],
  );

  const previewComponent = <DisplayText dir="auto">{productHierarchyDisplayName || '-'}</DisplayText>;

  const editComponent = (
    <Field<BasicDetails['productHierarchyLevelId']> name="productHierarchyLevelId">
      {field => (
        <StyledInputContainer>
          <MultiLevelSelect
            {...field.input}
            value={field.input.value ?? null}
            overriddenDisplayValue={truncateStringFromStart(productHierarchyDisplayName)}
            onChange={value => {
              field.input.onChange(value);

              handleRestrictedPropertyChange({ productHierarchyLevelId: value });

              if (
                (!value || !isOverTheCounterProductHierarchyLevel(value)) &&
                form.getFieldState('isRestrictedItem')?.value &&
                form.getFieldState('isOTCRestricted')?.value === true
              ) {
                form.change('isRestrictedItem', false);
                form.change('isOTCRestricted', false);
              }
            }}
            onClearClick={() => field.input.onChange(undefined)}
            options={productHierarchyOptions}
            placeholder={intl.formatMessage({ id: 'menu.selectOrSearch' })}
            disabled={!!values.productId}
            autoFocus={!values.productHierarchyLevelId}
          />
        </StyledInputContainer>
      )}
    </Field>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'menu-item.product-family' })}
      shouldDisplayCreateTag={!values.productHierarchyLevelId}
      previewComponent={previewComponent}
      editComponent={editComponent}
      sidePanelMode={sidePanelMode}
      offer={offer}
    />
  );
};
